// Single-next-box recommender for the dispatch pack screen.
//
// Inputs:
// - items[]: REMAINING (still-to-pack) items with sku, quantity, optional
//   cm dimensions. Already-packed items must be filtered out by the caller
//   so the recommendation reflects "what's left".
// - boxes[]: warehouse's available boxes with mm dimensions and tare
// - voidFillPct (e.g. 15): treat boxVolume × (1 - pct/100) as the usable
//   capacity, leaving headroom for void fill and imperfect packing
// - packingToleranceMm (e.g. 10): subtracted from each box dimension when
//   checking whether an item physically fits — accounts for the cardboard
//   walls + the operator not packing perfectly square
// - diagonal (Settings > Dispatch > Box Recommendation): when enabled, an
//   item that fails the straight axis-aligned check may still fit placed
//   DIAGONALLY — flat on the box floor, rotated towards the corners (or
//   corner-to-corner in a mailer). See "Diagonal placement" below.
//
// Algorithm:
//   1. Items lacking dimensions are skipped and reported via skippedSkus —
//      the recommendation is still computed from the dimensioned items so
//      the operator gets a starting point (the caller's banner shows the
//      skipped warning alongside the suggestion). Only when EVERY item
//      lacks dims is there no recommendation at all.
//      Exception — carton SKUs. A Jobalots line is a carton decanted at
//      the bench, and the product has no dims of its own; the BE supplies
//      `components` (the ASINs inside, with per-carton units and their
//      retail-package dims). Such an item expands into its components'
//      units (line quantity × units per carton) instead of being skipped.
//      A component without dims is skipped and reported as
//      "<sku>:<asin>" so the banner still counts it. An item that HAS its
//      own dims never consults components — its measurement wins.
//   2. When considerStock is on (Settings > Dispatch > Box Recommendation,
//      off by default), boxes with zero stock are excluded — never
//      recommend a box the operator can't grab. Off means stock is
//      ignored entirely, for warehouses that don't maintain box counts.
//   3. Try smallest-first: find the smallest box whose toleranced dims fit
//      every item, where every PAIR of items can physically coexist (a
//      side-by-side stacking check — volume alone lets two near-box-sized
//      slabs "fit" a box they can't both enter), AND whose usable volume
//      ≥ total remaining volume. If found, recommend it as a single box.
//      The volume/pair gates only apply to MULTI-unit orders — a single
//      item that passes the dimensional check physically fits, so void
//      fill never bumps a lone snug item into a bigger box.
//      A box that fails ONLY the volume gate is not given up on: void
//      fill is a blanket guess at wasted air, so packsTogether tries to
//      build a real arrangement, and the box is kept if it succeeds.
//      Raw volume overflow (items exceed the interior outright) is still
//      decided on the spot — no arrangement beats arithmetic.
//   4. Primary-large cap: when a box is flagged isPrimaryLarge, boxes with
//      a larger volume are dropped from consideration UNLESS some item
//      doesn't dimensionally fit the primary box — the primary is cheaper
//      and palletises better, so pure volume overflow means "another
//      primary box" (multi-box), never the mega box.
//   5. Otherwise (multi-box order) recommend the BIGGEST candidate box.
//      Operator packs as much as fits, then we recompute on the next scan
//      against the new "remaining" set — possibly recommending a smaller
//      box for the rest.
//   6. If even the biggest box can't dimensionally hold any single item,
//      return fits=false so the caller renders "no box fits, pick manually".
//   7. Mailbags are measured FLAT — length × width. Fit uses wrap-around
//      maths (see fitsInBag): closing the bag over contents consumes flat
//      material in both directions, so an item of thickness t needs
//      longest+t ≤ L and middle+t ≤ W. Multiple items lie flat in the
//      best of several packable arrangements (pure stack, base + layered
//      pile, side-by-side row — see bagArrangements); the bag takes the
//      set when any arrangement's bounding block wraps. Capacity ranking
//      uses the largest rigid box the bag can wrap (mailbagCapacityMm3).
//      Two kinds:
//        "mailbag" (plastic)  — 0mm material loss; thickness capped only
//                               when the catalogue sets maxFillMm (its
//                               heightMm is always 0 — see RecommenderBox).
//        "cardboard_mailbag"  — 5mm lost per flat axis (thicker board),
//                               heightMm is a MAX filled thickness so the
//                               mailer isn't bent out of shape.
//      Which containers an extra-protection ("fragile") item may not go
//      in is NOT inferred from the wall type — it's the per-warehouse
//      extraProtectionExcludedBoxes name list (Settings > Dispatch > Box
//      Recommendation), so any box can be ruled out, not just bags.
//   8. Diagonal placement (per-warehouse, ON by default; born from
//      JLI_0804_1 where a 51cm rod was sent to the 87-litre box H for
//      want of a straight 51cm run): a long item can lie flat on the
//      floor of a smaller box rotated towards the corners, or go
//      corner-to-corner in a mailer. Only FLAT, floor-plane rotation is
//      modelled — never 3D corner-to-corner tilts an operator can't pack
//      reliably. Diagonal checks use their own, LARGER packing
//      tolerances (diagonal.toleranceMm for rigid boxes,
//      diagonal.bagToleranceMm for BOTH mailer kinds — plastic and
//      cardboard are equally flat-packed wraps, so a diagonal item is
//      equally snug in either) because a tilted item meets the walls
//      corner-first.
//      Multi-item orders use a layer model: the diagonal item(s) claim
//      the floor lying flat side by side (widths summed), everything
//      else must fit in the remaining height above under the normal
//      axis-aligned rules. Recommendations that rely on a diagonal
//      placement name the SKUs in diagonalSkus so the pack screen can
//      tell the operator to place them diagonally — without the hint
//      they'd try it straight, conclude the tool is wrong, and grab the
//      big box anyway.
//
// Returns: { boxes, skippedSkus, diagonalSkus, fits }. `boxes` always
// contains 0 or 1 entries — the next box to use. We keep an array shape
// so the FE banner can stay simple.

// One ASIN inside a carton SKU — see DispatchOrderItem.components. `units`
// is per carton; the item's quantity multiplies it.
export interface RecommenderComponent {
    asin: string;
    units: number;
    lengthCm?: number | string | null;
    widthCm?: number | string | null;
    heightCm?: number | string | null;
}

export interface RecommenderItem {
    sku: string;
    quantity: number;
    lengthCm?: number | string | null;
    widthCm?: number | string | null;
    heightCm?: number | string | null;
    weightGrams?: number | null;
    // Product flag: this item is fragile, so the containers named in the
    // warehouse's extraProtectionExcludedBoxes list are off the table for
    // the whole shipment it lands in.
    extraProtection?: boolean;
    // Product flag (products.soft_item): clothing, costumes — anything
    // that shapes itself to a PLASTIC mailbag. There, closing the bag costs
    // it nothing on the flat axes, so only its footprint has to fit (see
    // softFitsInBag). Rigid boxes and cardboard mailers ignore it.
    soft?: boolean;
    // Carton contents, used ONLY when the item itself has no dims: the
    // recommender packs these units instead of skipping the line.
    components?: RecommenderComponent[];
}

// Per-warehouse setting (dispatch_extra_protection_excluded_boxes):
// dispatch-box NAMES an extra-protection item may not travel in. Matched
// case-insensitively and trimmed, because the names come from admin
// free-text. Empty = nothing excluded.
export const buildExcludedBoxSet = (names?: string[]): Set<string> =>
    new Set((names ?? []).map(n => String(n).trim().toUpperCase()).filter(n => n !== ""));

const isBoxExcluded = (name: string | undefined, excluded: Set<string>): boolean =>
    excluded.size > 0 && excluded.has(String(name ?? "").trim().toUpperCase());

export interface RecommenderBox {
    barcode: string;
    name: string;
    // EXTERNAL dimensions in mm — what an admin measures with a tape.
    // Internal usable space is reduced by wall_type (8mm single, 14mm
    // double) and the per-warehouse packing tolerance.
    lengthMm: number;
    widthMm: number;
    heightMm: number;
    weightGrams: number;
    currentStock: number;
    // "mailbag" = plastic mailer (flat, no height). "cardboard_mailbag" =
    // board-backed mailer: same wrap maths minus 5mm per flat axis, and
    // heightMm holds the MAX filled height.
    wallType?: "single" | "double" | "mailbag" | "cardboard_mailbag";
    // The warehouse's designated default "biggest" box (cheaper / palletises
    // better than the true giants). Boxes with a larger volume are only
    // recommended when an item's dimensions genuinely need them.
    isPrimaryLarge?: boolean;
    // What the box itself costs (dispatch_boxes.unit_cost). Drives the
    // second ranking key — see chooseCheapest. null / undefined means
    // "not recorded", which ranks BEHIND any priced box that also fits.
    unitCost?: number | null;
    // Optional ceiling on contents weight (dispatch_boxes.max_weight_grams).
    // Pass 1 skips a box whose limit the order's total weight exceeds;
    // null / undefined = no box limit.
    maxWeightGrams?: number | null;
    // Plastic mailbags only (dispatch_boxes.max_fill_mm): the maximum
    // FILLED thickness the bag may be asked to take. The wrap maths alone
    // will close a 545×453 bag over a 190 mm block; packers will not
    // (P2 acceptance fell from ~75% to 42% past 150 mm). null = uncapped.
    // Deliberately NOT heightMm — a height on a mailbag leaks into
    // volumetric weight and carrier payloads, and is forced to 0.
    maxFillMm?: number | null;
}

// Pass 1 (single-container) tiebreak: the SMALLEST container that fits
// wins unless a bigger one is cheaper by MORE than this (postage + box,
// in the tariff's currency). Born from Pembroke, 2026-10-19: InPost
// prices every container at the same floor, so the ranking collapsed to
// unit cost and box H (£0.79) beat the 8%-smaller G (£0.83) by 4p — 168
// "smaller box fits" overrides in 28 days, every one of them right about
// the box and wrong about nothing but 4p. Void fill and handling on the
// bigger box are worth more than that. 0 restores pure cheapest-first.
export const DEFAULT_SMALLER_BOX_MARGIN = 0.10;

// One shipping option the bench can reach, reduced from the warehouse's
// shipping rules + their target service's tariff (GET /carriers/shipping-tiers).
// `boxNames` null = the rule has no box-name restriction.
export interface ShippingTier {
    serviceCode?: string;
    cost: number;
    boxNames: string[] | null;
    maxWeightGrams: number | null;
    maxPieces: number | null;
}

// APC bills on the greater of actual and volumetric weight, and the
// service tariff caps test that figure — so a big empty box can price
// itself out of a cheap service. Mirrors shippingRules.volumetricWeightGrams
// on the server; keep the two in step.
const boxVolumetricGrams = (box: { lengthMm: number; widthMm: number; heightMm: number }): number => {
    const l = box.lengthMm / 10, w = box.widthMm / 10, h = box.heightMm / 10;
    if (l <= 0 || w <= 0 || h <= 0) return 0;
    return Math.round(((l * w * h) / 6000) * 1000);
};

// Cheapest shipping this box can attract for a consignment of `grams`.
// Takes the MINIMUM across matching tiers rather than the first match, so
// the ranking can't be silently inverted by someone reordering the rules
// in Settings. Infinity = no service will carry it; 0 when no tier data
// was supplied, which makes the whole term inert.
export const cheapestShippingCost = (
    box: RecommenderBox,
    grams: number,
    tiers?: ShippingTier[],
): number => {
    if (!tiers || tiers.length === 0) return 0;
    const chargeable = Math.max(grams, boxVolumetricGrams(box));
    const name = String(box.name ?? "").trim().toUpperCase();
    let best = Infinity;
    for (const t of tiers) {
        if (t.boxNames !== null && !t.boxNames.some(n => String(n).trim().toUpperCase() === name)) continue;
        if (t.maxWeightGrams !== null && chargeable > t.maxWeightGrams) continue;
        if (t.maxPieces !== null && t.maxPieces < 1) continue;
        if (t.cost < best) best = t.cost;
    }
    return best;
};

// Per-warehouse diagonal-placement settings (Settings > Dispatch > Box
// Recommendation). The tolerances are used INSTEAD of packingToleranceMm
// when evaluating a diagonal fit — they default to double the standard
// tolerance because a tilted item meets the container corner-first and
// the geometry is less forgiving of a snug call.
export interface DiagonalOptions {
    enabled: boolean;
    // Rigid boxes.
    toleranceMm: number;
    // Mailbags — plastic AND cardboard. Both are flat-packed wraps, so a
    // diagonal item is equally snug in either; kept separate from rigid
    // boxes so the wrap containers can be given extra slack on their own.
    bagToleranceMm: number;
}

// Wall thickness subtracted from each axis to get the internal usable
// dimensions of the box. Defaults to single-wall when unset so legacy
// rows behave like the most common case. Plastic mailbags are effectively
// wall-less — the stated dims ARE the usable space.
export const WALL_REDUCTION_MM: Record<"single" | "double" | "mailbag" | "cardboard_mailbag", number> = {
    single: 8,
    double: 14,
    mailbag: 0,
    // Cardboard mailers are thicker material — 5mm lost per FLAT axis
    // (applied inside the wrap maths, not the box maths).
    cardboard_mailbag: 5,
};

const isBagType = (wallType?: string): boolean =>
    wallType === "mailbag" || wallType === "cardboard_mailbag";

export interface Recommendation {
    boxes: Array<{ barcode: string; name: string; quantity: number }>;
    skippedSkus: string[];
    // SKUs the recommended container only takes DIAGONALLY — the pack
    // screen surfaces these so the operator angles them instead of
    // concluding the recommendation is wrong. Empty when everything
    // fits straight (or diagonal placement is disabled).
    diagonalSkus: string[];
    fits: boolean;
}

interface ItemUnit {
    sku: string;
    dims: [number, number, number];   // sorted descending, in mm
    volume: number;                    // mm³
    extraProtection: boolean;          // excluded-box names are off-limits
    soft: boolean;                     // folds to a plastic mailbag (products.soft_item)
}

const toMm = (cm: number | string | null | undefined): number | null => {
    if (cm === null || cm === undefined || cm === "") return null;
    const n = Number(cm);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n * 10;
};

const sortedDesc = (a: number, b: number, c: number): [number, number, number] => {
    const arr = [a, b, c].sort((x, y) => y - x);
    return [arr[0], arr[1], arr[2]];
};

const fitsInDims = (
    item: [number, number, number],
    boxToleranced: [number, number, number],
): boolean =>
    item[0] <= boxToleranced[0] &&
    item[1] <= boxToleranced[1] &&
    item[2] <= boxToleranced[2];

// ── Diagonal (rotated-in-plane) maths ────────────────────────────────
// Does an a×b rectangle fit inside an A×B rectangle at ANY rotation?
// A rotated rectangle fits iff its axis-aligned bounding box does, so at
// angle θ it needs  p·cosθ + q·sinθ ≤ P  and  p·sinθ + q·cosθ ≤ Q.
// We sweep θ in 0.5° steps rather than using the closed-form threshold:
// the sweep is exact at each sampled angle and can only UNDER-report by
// a sub-millimetre margin on our box sizes — i.e. it errs towards a
// bigger box, never towards recommending one the item won't go into.
const rectFitsRotated = (a: number, b: number, A: number, B: number): boolean => {
    const p = Math.max(a, b), q = Math.min(a, b);
    const P = Math.max(A, B), Q = Math.min(A, B);
    if (P <= 0 || Q <= 0) return false;
    if (p <= P && q <= Q) return true;                    // fits straight
    // Rotation can't shrink the rectangle's short side below q, and the
    // container's diagonal bounds everything.
    if (q > Q || p * p + q * q > P * P + Q * Q) return false;
    for (let deg = 0.5; deg < 90; deg += 0.5) {
        const th = (deg * Math.PI) / 180;
        const c = Math.cos(th), s = Math.sin(th);
        if (p * c + q * s <= P && p * s + q * c <= Q) return true;
    }
    return false;
};

// Diagonal fit of one unit in a rigid box: any box face can be the floor
// (the axis-aligned check is equally orientation-agnostic — the operator
// stands the box to suit), any item face can be down, and the item's
// footprint may then rotate freely in the floor plane. Never a 3D tilt.
const unitFitsBoxDiagonal = (
    d: [number, number, number],
    box: [number, number, number],
): boolean => {
    for (let bv = 0; bv < 3; bv++) {
        const floorA = box[(bv + 1) % 3];
        const floorB = box[(bv + 2) % 3];
        for (let iv = 0; iv < 3; iv++) {
            if (d[iv] > box[bv]) continue;
            if (rectFitsRotated(d[(iv + 1) % 3], d[(iv + 2) % 3], floorA, floorB)) return true;
        }
    }
    return false;
};

// ── Mailbag maths ────────────────────────────────────────────────────
// A mailbag is measured FLAT (length × width; height doesn't exist until
// it's filled). Closing the bag over an item consumes flat material in
// BOTH directions — the fixed perimeter has to travel up and over the
// contents — so an item of thickness t fits only if:
//     longest + t ≤ bag length   AND   middle + t ≤ bag width
// (item rotated so its SMALLEST dim is the thickness, which is provably
// optimal). Sanity: a sheet of paper (t≈0) fits a bag of its own flat
// size; a 50mm book the bag's exact flat size fails on both axes.
// maxT caps the filled thickness — Infinity for plastic mailers (wrap
// maths alone constrains them), the admin-set max height for cardboard
// mailers (which shouldn't be bent out of shape).
const fitsInBag = (
    dims: [number, number, number],
    bagL: number,
    bagW: number,
    maxT: number = Infinity,
): boolean => dims[2] <= maxT && dims[0] + dims[2] <= bagL && dims[1] + dims[2] <= bagW;

// Diagonal variant: the wrap consumes t on both flat axes, leaving a
// (L−t)×(W−t) rectangle the item's footprint may sit in at any angle —
// at 0° this is exactly the straight check above.
const fitsInBagDiagonal = (
    dims: [number, number, number],
    bagL: number,
    bagW: number,
    maxT: number = Infinity,
): boolean =>
    dims[2] <= maxT && rectFitsRotated(dims[0], dims[1], bagL - dims[2], bagW - dims[2]);

// Soft items in a PLASTIC mailbag (products.soft_item). A garment or a
// costume is not a rigid block: it shapes itself to the film, so the wrap
// rule above — t charged on both flat axes — does not apply and only the
// footprint has to fit. Two bounds keep that honest: the bag's own fill
// cap (maxT), and the bag's wrappable volume — the most any arrangement
// can hold (mailbagCapacityMm3) — so "only the footprint" never lets a
// pile of soft things into a bag that could not close over them.
// Born from Pembroke, 2026-10-28: every Bodysocks costume is catalogued
// 35 x 25 x 5 cm, the wrap rule asked 400 x 300 mm of a 375 x 300 P1, and
// packers overrode the P2 recommendation to a P1 170 times in 60 days.
const softFitsInBag = (
    block: [number, number, number],
    volume: number,
    bag: BagMeta,
): boolean =>
    block[2] <= bag.maxT &&
    block[0] <= bag.l && block[1] <= bag.w &&
    volume <= mailbagCapacityMm3(bag.l, bag.w, bag.maxT);

const softFitsInBagDiagonal = (
    block: [number, number, number],
    volume: number,
    bag: BagMeta,
): boolean =>
    block[2] <= bag.maxT &&
    rectFitsRotated(block[0], block[1], bag.l, bag.w) &&
    volume <= mailbagCapacityMm3(bag.l, bag.w, bag.maxT);

// Does an a×b footprint fit within an A×B footprint (90° rotation ok)?
const fits2D = (a: number, b: number, A: number, B: number): boolean =>
    Math.max(a, b) <= Math.max(A, B) && Math.min(a, b) <= Math.min(A, B);

// Multiple items in a bag lie FLAT in some arrangement. Each candidate
// below is the bounding block (footprint × thickness) of a REAL, packable
// arrangement — the bag takes the set when ANY candidate wraps like a
// single item. Every candidate is something a packer can actually do, so
// adding candidates only removes false rejections, never adds risk.
//   1. Pure stack: footprint of the biggest item, thicknesses summed.
//      (The original — and only — model until JLI_0804_15, where it
//      charged 203mm of thickness against a real pile of 95mm.)
//   2. Base + pile: the largest-footprint item is the base; the rest
//      group greedily into side-by-side layers that stay within the
//      base's footprint, each layer adding only its thickest member.
//      "One big flat thing with the smalls arranged on top."
//      Skipped when some item overhangs the base.
//   3. One side-by-side row: everything next to each other, thickness
//      of the thickest single item.
const bagArrangements = (units: ItemUnit[]): Array<[number, number, number]> => {
    let maxA = 0;
    let maxB = 0;
    let sumB = 0;
    let sumT = 0;
    let maxT = 0;
    for (const u of units) {
        maxA = Math.max(maxA, u.dims[0]);
        maxB = Math.max(maxB, u.dims[1]);
        sumB += u.dims[1];
        sumT += u.dims[2];
        maxT = Math.max(maxT, u.dims[2]);
    }
    const out: Array<[number, number, number]> = [
        [maxA, maxB, sumT],
        [Math.max(maxA, sumB), Math.min(maxA, sumB), maxT],
    ];

    const byArea = [...units].sort((x, y) =>
        (y.dims[0] * y.dims[1]) - (x.dims[0] * x.dims[1]) || y.dims[2] - x.dims[2]);
    const base = byArea[0];
    let pileT = base.dims[2];
    let layer: { a: number; b: number; t: number } | null = null;
    let available = byArea.length > 1;
    for (let i = 1; i < byArea.length; i++) {
        const u = byArea[i];
        if (!fits2D(u.dims[0], u.dims[1], base.dims[0], base.dims[1])) {
            available = false;
            break;
        }
        if (layer && fits2D(Math.max(layer.a, u.dims[0]), layer.b + u.dims[1], base.dims[0], base.dims[1])) {
            layer = { a: Math.max(layer.a, u.dims[0]), b: layer.b + u.dims[1], t: Math.max(layer.t, u.dims[2]) };
        } else {
            if (layer) pileT += layer.t;
            layer = { a: u.dims[0], b: u.dims[1], t: u.dims[2] };
        }
    }
    if (available) {
        if (layer) pileT += layer.t;
        out.push([base.dims[0], base.dims[1], pileT]);
    }
    return out;
};

// Usable bag geometry after material loss + tolerance, plus the rules
// that differ between the two mailer kinds.
interface BagMeta {
    l: number;          // usable flat length (≥ w)
    w: number;          // usable flat width
    maxT: number;       // max filled thickness (Infinity for plastic)
    plastic: boolean;   // film, so soft items shape to it (softFitsInBag)
}

const toBagMeta = (
    box: { lengthMm: number; widthMm: number; heightMm: number; wallType?: string; maxFillMm?: number | null },
    tolMm: number,
): BagMeta => {
    const plastic = box.wallType === "mailbag";
    const wallMm = plastic ? WALL_REDUCTION_MM.mailbag : WALL_REDUCTION_MM.cardboard_mailbag;
    const a = Math.max(0, box.lengthMm - wallMm - tolMm);
    const b = Math.max(0, box.widthMm - wallMm - tolMm);
    // Plastic: the optional fill cap (never heightMm, which is forced to
    // 0 on mailbags). Cardboard: heightMm IS the max filled height.
    const plasticCap = Number(box.maxFillMm);
    return {
        l: Math.max(a, b),
        w: Math.min(a, b),
        maxT: plastic ? (plasticCap > 0 ? plasticCap : Infinity) : box.heightMm,
        plastic,
    };
};

// Ranking capacity for a bag: the volume of the largest rigid box it can
// wrap. With the shrink model above, a box of thickness t gets footprint
// (L−t)×(W−t); maximising (L−t)(W−t)t gives
//     t* = ((L+W) − √(L² − LW + W²)) / 3
// Used so bags sort sensibly against rigid boxes in the smallest-first
// pass (a 375×300 bag ranks near a mid-size box, not at zero).
export const mailbagCapacityMm3 = (bagL: number, bagW: number, maxT: number = Infinity): number => {
    if (bagL <= 0 || bagW <= 0) return 0;
    const t = Math.min(
        maxT,
        (bagL + bagW - Math.sqrt(bagL * bagL - bagL * bagW + bagW * bagW)) / 3,
    );
    if (t <= 0) return 0;
    return (bagL - t) * (bagW - t) * t;
};

// Can two units physically coexist in the box? Try placing them side by
// side along each axis: the composite block sums that axis and takes the
// max of the other two, then gets the same rotation-agnostic sorted
// comparison as a single item. Deliberately conservative — a pair that
// would only fit via exotic interlocking is treated as not fitting, which
// just bumps the recommendation to the next size up rather than sending
// the operator to fetch a box the items won't go into.
const pairCoexists = (
    a: [number, number, number],
    b: [number, number, number],
    boxToleranced: [number, number, number],
): boolean => {
    for (let axis = 0; axis < 3; axis++) {
        const composite = sortedDesc(
            axis === 0 ? a[0] + b[0] : Math.max(a[0], b[0]),
            axis === 1 ? a[1] + b[1] : Math.max(a[1], b[1]),
            axis === 2 ? a[2] + b[2] : Math.max(a[2], b[2]),
        );
        if (fitsInDims(composite, boxToleranced)) return true;
    }
    return false;
};

// ── Constructive packing proof ───────────────────────────────────────
// The void-fill percentage is a guess at how much air a mixed set of
// items wastes, applied uniformly however well those particular items
// happen to tessellate. When it says no, that is only ever an ESTIMATE —
// so before sending the operator for a bigger box we try to actually
// BUILD an arrangement in the smaller one.
//
// Extreme-point first-fit-decreasing: items are placed largest-first at
// candidate corner points, each tried in all six axis-aligned
// orientations, taking the deepest-bottom-left placement that neither
// overlaps a placed item nor leaves itself floating.
//
// The result is one-sided, in the safe direction. SUCCESS means we hold
// a real, physically packable arrangement, so the box genuinely takes
// the set. FAILURE means only that this heuristic didn't find one. That
// asymmetry is why the proof may RESCUE a volume-gate rejection but is
// never run to overturn a volume-gate acceptance — a box the percentage
// already approved keeps its approval.
//
// Mirrors what the mailbag path has always done: bagArrangements tests
// real, packable arrangements rather than trusting a volume fraction.

interface PlacedUnit { x: number; y: number; z: number; dx: number; dy: number; dz: number }

// The six axis-aligned orientations, as permutations of the item's
// (already sorted-descending) dimensions.
const ORIENTATIONS: ReadonlyArray<readonly [number, number, number]> = [
    [0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0],
];

// Fraction of an item's base that must rest on the floor or on the tops
// of already-placed items. Without it the search happily floats a box in
// mid-air — an arrangement the operator cannot reproduce at the bench.
const MIN_SUPPORT_RATIO = 0.7;

// Dimensions are millimetres carried through a cm→mm multiply, so exact
// float equality can't be relied on when comparing a stacked item's base
// against the top it rests on.
const PACK_EPS = 1e-6;

// Above this many units the proof is skipped and the volume gate's
// verdict stands. An order with this much left to pack is a multi-box
// order — the single-box pass is hopeless anyway, so the work would be
// spent to reach the same answer.
const PACK_UNIT_CAP = 32;

// Hard ceiling on placement attempts per call, so a pathological item mix
// can't stall the pack screen between scans.
const PACK_ATTEMPT_BUDGET = 50000;

const unitsOverlap = (a: PlacedUnit, b: PlacedUnit): boolean =>
    a.x < b.x + b.dx - PACK_EPS && b.x < a.x + a.dx - PACK_EPS &&
    a.y < b.y + b.dy - PACK_EPS && b.y < a.y + a.dy - PACK_EPS &&
    a.z < b.z + b.dz - PACK_EPS && b.z < a.z + a.dz - PACK_EPS;

const isSupported = (c: PlacedUnit, placed: PlacedUnit[]): boolean => {
    if (c.z <= PACK_EPS) return true;                       // on the floor
    const needed = c.dx * c.dy * MIN_SUPPORT_RATIO;
    let area = 0;
    for (const p of placed) {
        if (Math.abs(p.z + p.dz - c.z) > PACK_EPS) continue;   // not the surface it sits on
        const ox = Math.min(c.x + c.dx, p.x + p.dx) - Math.max(c.x, p.x);
        const oy = Math.min(c.y + c.dy, p.y + p.dy) - Math.max(c.y, p.y);
        if (ox > 0 && oy > 0) area += ox * oy;
        if (area >= needed) return true;
    }
    return false;
};

// Can a real arrangement of these units be built inside a box of these
// (already wall- and tolerance-reduced) dimensions? See the note above:
// true is a proof, false is only a failure to find one.
export const packsTogether = (units: ItemUnit[], box: [number, number, number]): boolean => {
    const [boxX, boxY, boxZ] = box;
    if (boxX <= 0 || boxY <= 0 || boxZ <= 0) return false;
    if (units.length > PACK_UNIT_CAP) return false;

    const placed: PlacedUnit[] = [];
    let points: Array<[number, number, number]> = [[0, 0, 0]];
    let budget = PACK_ATTEMPT_BUDGET;

    for (const u of [...units].sort((a, b) => b.volume - a.volume)) {
        // Deepest-bottom-left: fill the floor before building upwards, so
        // the arrangement stays one a packer would naturally produce.
        points.sort((p, q) => p[2] - q[2] || p[1] - q[1] || p[0] - q[0]);

        let chosen: PlacedUnit | null = null;
        for (const [px, py, pz] of points) {
            for (const o of ORIENTATIONS) {
                if (--budget < 0) return false;
                const dx = u.dims[o[0]], dy = u.dims[o[1]], dz = u.dims[o[2]];
                if (px + dx > boxX + PACK_EPS) continue;
                if (py + dy > boxY + PACK_EPS) continue;
                if (pz + dz > boxZ + PACK_EPS) continue;
                const candidate: PlacedUnit = { x: px, y: py, z: pz, dx, dy, dz };
                if (placed.some(p => unitsOverlap(candidate, p))) continue;
                if (!isSupported(candidate, placed)) continue;
                chosen = candidate;
                break;
            }
            if (chosen) break;
        }
        if (!chosen) return false;

        placed.push(chosen);
        // The consumed corner is replaced by the three it opens up.
        const spent = chosen;
        points = points.filter(p => p[0] !== spent.x || p[1] !== spent.y || p[2] !== spent.z);
        for (const p of [
            [spent.x + spent.dx, spent.y, spent.z] as [number, number, number],
            [spent.x, spent.y + spent.dy, spent.z] as [number, number, number],
            [spent.x, spent.y, spent.z + spent.dz] as [number, number, number],
        ]) {
            if (p[0] >= boxX || p[1] >= boxY || p[2] >= boxZ) continue;
            if (points.some(q => q[0] === p[0] && q[1] === p[1] && q[2] === p[2])) continue;
            points.push(p);
        }
    }
    return true;
};

// ── Shared container metadata + hold checks ──────────────────────────
// One precomputed shape for both recommendBoxes and fitsInSingleBox so
// the two can never drift apart on the fit rules.
interface ContainerMeta {
    volume: number;                                  // usable-space ranking
    sortedToleranced: [number, number, number];      // straight fits
    sortedDiagToleranced: [number, number, number];  // diagonal fits
    // Set for mailbags: usable FLAT dims (minus material loss and
    // tolerance) plus the thickness cap / plastic rule. Bags have no
    // rigid height — fit uses the wrap-around maths instead of box
    // internals. bagDiag is the same geometry under the diagonal
    // tolerance (maxT is a physical cap, so it never changes).
    bag?: BagMeta;
    bagDiag?: BagMeta;
    // This container's name is on the warehouse's excluded list, so it
    // can't take a shipment containing any extra-protection item.
    excludedForExtraProtection: boolean;
}

const buildContainerMeta = (
    box: { name?: string; lengthMm: number; widthMm: number; heightMm: number; wallType?: string; maxFillMm?: number | null },
    tolMm: number,
    diag: DiagonalOptions,
    excludedBoxes: Set<string>,
): ContainerMeta | null => {
    const excludedForExtraProtection = isBoxExcluded(box.name, excludedBoxes);
    if (isBagType(box.wallType)) {
        if (!(box.lengthMm > 0 && box.widthMm > 0)) return null;
        if (box.wallType === "cardboard_mailbag" && !(box.heightMm > 0)) return null;
        const bag = toBagMeta(box, tolMm);
        const bagDiag = toBagMeta(box, Math.max(0, diag.bagToleranceMm));
        const volume = mailbagCapacityMm3(bag.l, bag.w, bag.maxT);
        if (volume <= 0) return null;
        return {
            volume,
            sortedToleranced: [0, 0, 0],
            sortedDiagToleranced: [0, 0, 0],
            bag,
            bagDiag,
            excludedForExtraProtection,
        };
    }
    if (!(box.lengthMm > 0 && box.widthMm > 0 && box.heightMm > 0)) return null;
    const wallMm = WALL_REDUCTION_MM[(box.wallType as keyof typeof WALL_REDUCTION_MM) ?? "single"];
    const internalL = Math.max(0, box.lengthMm - wallMm);
    const internalW = Math.max(0, box.widthMm - wallMm);
    const internalH = Math.max(0, box.heightMm - wallMm);
    const volume = internalL * internalW * internalH;
    if (volume <= 0) return null;
    const diagTol = Math.max(0, diag.toleranceMm);
    return {
        volume,
        sortedToleranced: sortedDesc(
            Math.max(0, internalL - tolMm),
            Math.max(0, internalW - tolMm),
            Math.max(0, internalH - tolMm),
        ),
        sortedDiagToleranced: sortedDesc(
            Math.max(0, internalL - diagTol),
            Math.max(0, internalW - diagTol),
            Math.max(0, internalH - diagTol),
        ),
        excludedForExtraProtection,
    };
};

// Can this single unit go into this container at all (straight, or
// diagonally when enabled)? Used for the primary-large cap and the
// "does anything fit the biggest box" sanity check.
const unitFitsContainer = (u: ItemUnit, m: ContainerMeta, diagEnabled: boolean): boolean => {
    // An excluded container is off-limits to a fragile item however it's
    // placed — check before any geometry.
    if (m.excludedForExtraProtection && u.extraProtection) return false;
    if (m.bag) {
        if (u.soft && m.bag.plastic) {
            if (softFitsInBag(u.dims, u.volume, m.bag)) return true;
            return diagEnabled && softFitsInBagDiagonal(u.dims, u.volume, m.bagDiag!);
        }
        if (fitsInBag(u.dims, m.bag.l, m.bag.w, m.bag.maxT)) return true;
        if (!diagEnabled) return false;
        return fitsInBagDiagonal(u.dims, m.bagDiag!.l, m.bagDiag!.w, m.bagDiag!.maxT);
    }
    if (fitsInDims(u.dims, m.sortedToleranced)) return true;
    return diagEnabled && unitFitsBoxDiagonal(u.dims, m.sortedDiagToleranced);
};

const NO_HOLD: { ok: false; diagonalSkus: string[] } = { ok: false, diagonalSkus: [] };

// Would ALL these units fit together in this ONE container? Returns the
// SKUs that rely on a diagonal placement when it does.
const containerHoldsAll = (
    units: ItemUnit[],
    m: ContainerMeta,
    voidPct: number,
    diagEnabled: boolean,
): { ok: boolean; diagonalSkus: string[] } => {
    // A container on the warehouse's excluded list can't carry the
    // shipment if ANY unit in it is flagged for extra protection — the
    // whole set travels in one container, so one fragile item rules it
    // out (straight OR diagonal).
    if (m.excludedForExtraProtection && units.some(u => u.extraProtection)) return NO_HOLD;

    // Mailbag: wrap-around maths on the FLAT dims (height doesn't exist);
    // the flat-stack model doubles as the capacity check. Cardboard adds
    // the 5mm/axis material loss + max-thickness cap.
    if (m.bag) {
        const bag = m.bag;
        const bagDiag = m.bagDiag!;
        // Every unit soft and the bag plastic: footprint, fill cap and the
        // bag's wrappable volume (softFitsInBag). One rigid unit puts the
        // whole set back on the wrap rule — a mixed pile is a rigid pile.
        const soft = bag.plastic && units.every(u => u.soft);
        const totalVolume = units.reduce((s, u) => s + u.volume, 0);
        const straight = (b: [number, number, number], vol: number): boolean =>
            soft ? softFitsInBag(b, vol, bag) : fitsInBag(b, bag.l, bag.w, bag.maxT);
        const angled = (b: [number, number, number], vol: number): boolean =>
            soft ? softFitsInBagDiagonal(b, vol, bagDiag) : fitsInBagDiagonal(b, bagDiag.l, bagDiag.w, bagDiag.maxT);
        const diagSkus = new Set<string>();
        for (const u of units) {
            if (straight(u.dims, u.volume)) continue;
            if (!diagEnabled) return NO_HOLD;
            if (!angled(u.dims, u.volume)) return NO_HOLD;
            diagSkus.add(u.sku);
        }
        if (units.length > 1) {
            // Try every packable arrangement straight; if none wraps,
            // try each diagonally (a unit that individually needed
            // rotation always lands here — the group constraints are
            // strictly harder than its own).
            const blocks = bagArrangements(units);
            if (!blocks.some(b => straight(b, totalVolume))) {
                if (!diagEnabled) return NO_HOLD;
                if (!blocks.some(b => angled(b, totalVolume))) return NO_HOLD;
                units.forEach(u => diagSkus.add(u.sku));
            }
        }
        return { ok: true, diagonalSkus: Array.from(diagSkus) };
    }

    // Rigid box. Split straight-fitting units from diagonal-only ones.
    const axisUnits: ItemUnit[] = [];
    const diagUnits: ItemUnit[] = [];
    for (const u of units) {
        (fitsInDims(u.dims, m.sortedToleranced) ? axisUnits : diagUnits).push(u);
    }
    if (diagUnits.length > 0 && !diagEnabled) return NO_HOLD;

    // Void-fill headroom is a multi-unit concern (air gaps between
    // items); a lone unit that fits dimensionally fits, full stop.
    //
    // Failing it is NOT final. The percentage is a blanket estimate of
    // wasted air that takes no account of how well these particular
    // items tessellate, and it was over-sizing real orders — JLI 336833
    // sent eight units to box I on volume alone, every dimensional check
    // against the smaller box H having passed, and box H is what the
    // operator then packed them into. So a rejection here downgrades to
    // a demand for PROOF: an arrangement packsTogether can actually
    // build. Only raw volume overflow is decided here outright.
    let needsPackingProof = false;
    if (units.length > 1) {
        const totalVolume = units.reduce((s, u) => s + u.volume, 0);
        // No arrangement beats raw volume — this one is arithmetic, not
        // an estimate, so there is nothing for the packer to rescue.
        if (totalVolume > m.volume) return NO_HOLD;
        needsPackingProof = m.volume * (1 - voidPct / 100) < totalVolume;
    }

    if (diagUnits.length === 0) {
        for (let i = 0; i < units.length; i++) {
            for (let j = i + 1; j < units.length; j++) {
                if (!pairCoexists(units[i].dims, units[j].dims, m.sortedToleranced)) return NO_HOLD;
            }
        }
        if (needsPackingProof && !packsTogether(units, m.sortedToleranced)) return NO_HOLD;
        return { ok: true, diagonalSkus: [] };
    }

    // A lone diagonal unit gets the full orientation search.
    if (units.length === 1) {
        return unitFitsBoxDiagonal(units[0].dims, m.sortedDiagToleranced)
            ? { ok: true, diagonalSkus: [units[0].sku] }
            : NO_HOLD;
    }

    // Layer model for multi-item orders: the diagonal units lie FLAT on
    // the box floor (largest two box dims), side by side — footprint =
    // longest dim × summed widths, rotated as one block. Everything else
    // packs into the remaining height above under the normal
    // axis-aligned rules. Conservative: a mix that would only work with
    // the diagonal item wedged mid-stack is treated as not fitting.
    let layerL = 0;
    let layerW = 0;
    let layerH = 0;
    for (const u of diagUnits) {
        layerL = Math.max(layerL, u.dims[0]);
        layerW += u.dims[1];
        layerH = Math.max(layerH, u.dims[2]);
    }
    const [dFloorA, dFloorB, dHeight] = m.sortedDiagToleranced;
    if (layerH > dHeight) return NO_HOLD;
    if (!rectFitsRotated(layerL, layerW, dFloorA, dFloorB)) return NO_HOLD;

    const residual = sortedDesc(
        m.sortedToleranced[0],
        m.sortedToleranced[1],
        Math.max(0, m.sortedToleranced[2] - layerH),
    );
    for (const u of axisUnits) {
        if (!fitsInDims(u.dims, residual)) return NO_HOLD;
    }
    for (let i = 0; i < axisUnits.length; i++) {
        for (let j = i + 1; j < axisUnits.length; j++) {
            if (!pairCoexists(axisUnits[i].dims, axisUnits[j].dims, residual)) return NO_HOLD;
        }
    }
    // The diagonal units' floor layer is already measured out above, so
    // the proof only has to seat what stacks in the height left over.
    if (needsPackingProof && !packsTogether(axisUnits, residual)) return NO_HOLD;
    return { ok: true, diagonalSkus: Array.from(new Set(diagUnits.map(u => u.sku))) };
};

const DIAGONAL_OFF: DiagonalOptions = { enabled: false, toleranceMm: 10, bagToleranceMm: 10 };

const pushUnits = (
    units: ItemUnit[],
    sku: string,
    lMm: number,
    wMm: number,
    hMm: number,
    qty: number,
    extraProtection: boolean,
    soft = false,
): void => {
    const dims = sortedDesc(lMm, wMm, hMm);
    const volume = lMm * wMm * hMm;
    for (let i = 0; i < qty; i++) {
        units.push({ sku, dims, volume, extraProtection, soft });
    }
};

// The label a carton component is reported under (skippedSkus /
// diagonalSkus) — the line's SKU the operator sees on the order, then the
// ASIN of the item inside it they need to pick out.
export const componentLabel = (sku: string, asin: string): string => `${sku}:${asin}`;

const expandUnits = (items: RecommenderItem[], skipped?: Set<string>): ItemUnit[] | null => {
    const units: ItemUnit[] = [];
    for (const item of items) {
        const qty = Math.max(0, Math.floor(item.quantity));
        const fragile = item.extraProtection === true;
        const lMm = toMm(item.lengthCm);
        const wMm = toMm(item.widthCm);
        const hMm = toMm(item.heightCm);
        if (lMm != null && wMm != null && hMm != null) {
            pushUnits(units, item.sku, lMm, wMm, hMm, qty, fragile, item.soft === true);
            continue;
        }
        // Carton contents below stay rigid: the flag describes the product,
        // and nothing says the items inside a carton are soft.
        // No dims of its own. A carton SKU packs as its contents — every
        // component's units, times the number of cartons on the line.
        const components = item.components ?? [];
        if (components.length === 0) {
            if (!skipped) return null;   // caller wants a hard fail on missing dims
            skipped.add(item.sku);
            continue;
        }
        for (const c of components) {
            const label = componentLabel(item.sku, c.asin);
            const cl = toMm(c.lengthCm);
            const cw = toMm(c.widthCm);
            const ch = toMm(c.heightCm);
            if (cl == null || cw == null || ch == null) {
                if (!skipped) return null;
                skipped.add(label);
                continue;
            }
            const perCarton = Math.max(0, Math.floor(Number(c.units) || 0));
            pushUnits(units, label, cl, cw, ch, qty * perCarton, fragile);
        }
    }
    return units;
};

// Would these items all fit together inside this ONE box? Same fit rules
// as the recommender (external dims − wall − tolerance, per-item fit,
// pairwise coexistence, void-fill volume headroom, diagonal placement).
// The pack screen uses this to decide whether a "next box" suggestion is
// needed at all: if everything still to pack fits the box already in use
// (together with what's in it), suggesting another box would just
// mislead the operator. Items with unknown dimensions return false —
// can't verify, keep the suggestion.
export function fitsInSingleBox(input: {
    items: RecommenderItem[];
    // `name` is what the excluded-box list matches on; a custom-dimension
    // box at the bench has none and is never excluded.
    box: { name?: string; lengthMm: number; widthMm: number; heightMm: number; wallType?: "single" | "double" | "mailbag" | "cardboard_mailbag"; maxFillMm?: number | null };
    voidFillPct: number;
    packingToleranceMm: number;
    diagonal?: DiagonalOptions;
    extraProtectionExcludedBoxes?: string[];
}): boolean {
    const { items, box, voidFillPct, packingToleranceMm, diagonal = DIAGONAL_OFF, extraProtectionExcludedBoxes } = input;
    const tolMm = Math.max(0, packingToleranceMm);
    const voidPct = Math.max(0, Math.min(99, voidFillPct));

    const units = expandUnits(items);
    if (units === null) return false;
    if (units.length === 0) return true;

    const meta = buildContainerMeta(box, tolMm, diagonal, buildExcludedBoxSet(extraProtectionExcludedBoxes));
    if (!meta) return false;
    return containerHoldsAll(units, meta, voidPct, diagonal.enabled).ok;
}

export function recommendBoxes(input: {
    items: RecommenderItem[];
    boxes: RecommenderBox[];
    voidFillPct: number;
    packingToleranceMm: number;
    // Per-warehouse setting (dispatch_stock_aware_recommendation),
    // default off: recommend regardless of stock levels.
    considerStock?: boolean;
    // Per-warehouse diagonal-placement settings; omitted = disabled so
    // existing callers keep the straight-fit behaviour.
    diagonal?: DiagonalOptions;
    // Per-warehouse setting (dispatch_extra_protection_excluded_boxes):
    // box NAMES that may not carry an extra-protection item. Omitted /
    // empty = no container is ruled out on fragility grounds.
    extraProtectionExcludedBoxes?: string[];
    // The warehouse's shipping options. Supplied → the single-box pass
    // picks the CHEAPEST container that fits (shipping first, then the
    // box's own unit cost) instead of merely the smallest. Omitted →
    // smallest-first, exactly as before.
    shippingTiers?: ShippingTier[];
    // How much cheaper a BIGGER container must be to beat a smaller one
    // that also fits. See DEFAULT_SMALLER_BOX_MARGIN.
    smallerBoxMargin?: number;
}): Recommendation {
    const { items, boxes, voidFillPct, packingToleranceMm, considerStock = false, diagonal = DIAGONAL_OFF, extraProtectionExcludedBoxes, shippingTiers, smallerBoxMargin = DEFAULT_SMALLER_BOX_MARGIN } = input;

    const voidPct = Math.max(0, Math.min(99, voidFillPct));
    const tolMm = Math.max(0, packingToleranceMm);
    const excludedBoxes = buildExcludedBoxSet(extraProtectionExcludedBoxes);

    // Filter & precompute box metadata. Stored dimensions are EXTERNAL —
    // subtract the cardboard wall thickness (8mm single / 14mm double)
    // AND the per-warehouse packing tolerance to get the toleranced
    // internal dimensions used for fit checks. Volume uses the same
    // post-wall-reduction internal volume so the void-fill % applies to
    // actual usable space, not to the cardboard. Drop any box whose
    // post-reduction dimensions go non-positive (admin error). Sort
    // ascending by usable volume so single-box pass picks the smallest.
    type BoxMeta = ContainerMeta & { type: RecommenderBox };
    const validBoxes: BoxMeta[] = boxes
        .filter(b => !considerStock || (b.currentStock ?? 0) > 0)
        .flatMap(b => {
            const meta = buildContainerMeta(b, tolMm, diagonal, excludedBoxes);
            return meta ? [{ ...meta, type: b }] : [];
        })
        .sort((x, y) => x.volume - y.volume);

    if (validBoxes.length === 0) {
        return { boxes: [], skippedSkus: [], diagonalSkus: [], fits: false };
    }

    // Expand items into one entry per unit, capturing dims + skipping any
    // that lack dimensions. Items missing dims are excluded from the
    // maths but reported so the caller's banner can warn alongside the
    // suggestion. Only bail when NOTHING has dimensions — a partial
    // recommendation beats none.
    const skippedSet = new Set<string>();
    const units = expandUnits(items, skippedSet)!;
    const skippedSkus = Array.from(skippedSet);

    // Nothing (with dims) left to pack → no recommendation.
    if (units.length === 0) {
        return { boxes: [], skippedSkus, diagonalSkus: [], fits: true };
    }

    // Drop excluded containers outright once the set contains a fragile
    // item, rather than relying on each pass to check: pass 2 recommends
    // the BIGGEST candidate without asking whether it holds everything,
    // so an excluded container left in the list could still be suggested.
    // Not over-strict for multi-box orders — once the fragile item has
    // been packed the recommender recomputes against the remainder and
    // the excluded containers come back.
    const anyExtraProtection = units.some(u => u.extraProtection);
    let candidates = anyExtraProtection
        ? validBoxes.filter(b => !b.excludedForExtraProtection)
        : validBoxes;
    if (candidates.length === 0) {
        return { boxes: [], skippedSkus, diagonalSkus: [], fits: false };
    }

    // Primary-large cap: boxes bigger than the designated primary are only
    // in play when some item genuinely needs their dimensions — a diagonal
    // fit in the primary counts as fitting. (Items with unknown dims were
    // already skipped above and can't lift the cap.)
    const primary = candidates.find(b => b.type.isPrimaryLarge === true);
    if (primary) {
        const someItemNeedsBigger = units.some(u => !unitFitsContainer(u, primary, diagonal.enabled));
        if (!someItemNeedsBigger) {
            candidates = candidates.filter(b => b.volume <= primary.volume);
        }
    }

    // Pass 1: CHEAPEST single container that holds everything.
    //
    // Ranking keys, in order (per Jack, 2026-08-11):
    //   1. shipping — by far the bigger number (£2.64-£4.44 a parcel vs
    //      pennies for the box), and the box NAME is what gates the pack
    //      rates, so the container choice sets the postage;
    //   2. the box's own unit cost — P1 at £0.05 beats M2 at £0.40 when
    //      both fit and both ride MP16, which is the case this exists for;
    //   3. volume, as it always was.
    //
    // A box with no recorded unit cost sorts behind every priced box that
    // also fits, so populating costs progressively improves the choice.
    // With NO tier data and NO unit costs, every key but the last ties and
    // this degrades exactly to the previous smallest-first behaviour.
    //
    // Since 2026-10-19 the cost keys are a BUDGET rather than a strict
    // order: the cheapest fitting container sets it, and the SMALLEST
    // fitting container within `smallerBoxMargin` of that budget wins.
    // A bigger box therefore has to be cheaper by more than the margin
    // to be recommended over a smaller one that also fits — see
    // DEFAULT_SMALLER_BOX_MARGIN for the 4p H-over-G case this ends.
    const totalGrams = items.reduce(
        (sum, i) => sum + (Number(i.weightGrams) || 0) * Math.max(0, Math.floor(i.quantity)), 0,
    );
    const margin = Math.max(0, Number(smallerBoxMargin) || 0);
    // Postage + the box itself. Unpriced boxes are Infinity, so they can
    // only win when nothing priced fits.
    const totalCostOf = (b: BoxMeta): number =>
        cheapestShippingCost(b.type, totalGrams, shippingTiers)
        + (b.type.unitCost == null ? Infinity : Number(b.type.unitCost));
    // `candidates` is already in ascending usable-volume order.
    const holds: Array<{ box: BoxMeta; diagonalSkus: string[]; total: number }> = [];
    for (const box of candidates) {
        if (box.type.maxWeightGrams != null && totalGrams > box.type.maxWeightGrams) continue;
        const hold = containerHoldsAll(units, box, voidPct, diagonal.enabled);
        if (hold.ok) holds.push({ box, diagonalSkus: hold.diagonalSkus, total: totalCostOf(box) });
    }
    if (holds.length > 0) {
        const cheapest = Math.min(...holds.map(h => h.total));
        const budget = Number.isFinite(cheapest) ? cheapest + margin : Infinity;
        // Smallest within budget; equal volumes fall back to cheaper. The
        // epsilon keeps 2.69 + 0.10 from losing to 2.79 on float noise.
        const pick = holds
            .filter(h => h.total <= budget + 1e-9)
            .sort((x, y) => (x.box.volume - y.box.volume) || (x.total - y.total))[0];
        return {
            boxes: [{ barcode: pick.box.type.barcode, name: pick.box.type.name, quantity: 1 }],
            skippedSkus,
            diagonalSkus: pick.diagonalSkus,
            fits: true,
        };
    }

    // Pass 2: multi-box order — recommend the biggest candidate box (the
    // primary large box when one is designated and its dims suffice). The
    // operator packs as much as fits, then on the next scan we recompute
    // against a smaller "remaining" set and possibly suggest a smaller box.
    const biggest = candidates[candidates.length - 1];

    // Sanity: at least one remaining item must fit in the biggest box,
    // otherwise even our fallback is useless.
    const anyItemFits = units.some(u => unitFitsContainer(u, biggest, diagonal.enabled));
    if (!anyItemFits) {
        return { boxes: [], skippedSkus, diagonalSkus: [], fits: false };
    }

    // Flag the units the biggest box only takes diagonally, so the hint
    // still shows on multi-box orders.
    const diagonalSkus = diagonal.enabled
        ? Array.from(new Set(units
            .filter(u => !unitFitsContainer(u, biggest, false) && unitFitsContainer(u, biggest, true))
            .map(u => u.sku)))
        : [];

    return {
        boxes: [{ barcode: biggest.type.barcode, name: biggest.type.name, quantity: 1 }],
        skippedSkus,
        diagonalSkus,
        fits: true,
    };
}
